import { getCurrencySymbol } from '@angular/common';
import { INDRADateService } from '@ibe.services/date.indra.service';
import { SESSION_VAR_KEY_CURRENCY } from '@ibe.config';
import * as moment from 'moment';

export interface INDRACalendarOutboundPriceRequestParam {
	cabinClass: string;
	destination: String;
	market: string;
	origin: String;
	tripType: string;
	month?: number;
	year?: number; //UPDATE (INDRA-RV) - ADDITIONAL PARAM FOR RETRIEVAL OF OUTBOUND FLIGHTS PRICES
	payWithMiles?: boolean;
	starAlliance?: boolean;
	paxType?: string;
}

export interface INDRACalendarInboundPriceRequestParam {
	cabinClass: string;
	destination: String;
	market: string;
	origin: String;
	tripType: string;
	departureDate: string;
	payWithMiles?: boolean;
	starAlliance?: boolean;
	paxType?: string;
}

export interface INDRABFMBookingAvailabilityCalendarResponse {
	status: string;
	data: INDRABFMBestPriceForDateData;
	errors: any;
}

export interface INDRABFMBookingAvailabilityCalendarReturnsResponse {
	status: string;
	data: INDRABFMPriceReturnsData;
	errors: any;
}

export interface INDRABFMBookingMonthlyLowestPriceRequestParam {
	marketCode: string;
	origin: String;
	destination: String;
	tripType: string;
	payWithMiles?: boolean;
	starAlliance?: boolean;
	outbound?: string;
	paxType?: string
}

export interface INDRABFMBookingMonthlyLowestPriceResponse {
	status: string;
	errors: any;
	monthlyLowestPrices: INDRABFMMonthlyLowestPricesData;
}

export interface INDRABFMMonthlyLowestPricesData {
	data: INDRABFMMonthPriceData[];
}

export interface INDRABFMMonthPriceData {
	month: number;
	price: number;
}

export interface INDRABFMBestPriceForDateData {
	bestPriceForDates: INDRABFMBestPriceForDate[];
}

export interface INDRABFMPriceReturnsData {
	market: string;
	entity: number;
	origin: string;
	departureDate: string;
	directFlight: boolean;
	tripType: string;
	cabin: string;
	returns: INDRABFMReturnsForDate[];
}

export interface INDRABFMBestPriceForDate {
	arrivalAirport: string;
	bestTotalMiles: number;
	bestTotalPrice: number;
	cabinClass: string;
	currency: string;
	departureAirport: string;
	departureDate: Date;
	insertionDate: string;
	market: string;
	monthlyMinimum: string;
	returnDate: string;
	tripType: string;
	starAlliance: boolean;
}

export interface INDRABFMReturnsForDate {
	returnDate: string;
	price: number;
	miles: number;
}

export interface INDRADailyCost {
	date: Date;
	ammount: {
		currency: {
			value: number;
			unit: string;
			name: string;
			highlightSymbol?: string;
		};
		points: number;
		minMax?: {
			currency: {
				monthlyMaximum: boolean;
				monthlyMinimum: boolean;
			},
			points: {
				monthlyMaximum: boolean;
				monthlyMinimum: boolean;
			}
		}
	};
	no_flights?: boolean;
	sold_out?: boolean;
	stepIndex?: number;
	dateValue?: number;
}

export interface INDRAAmmount {
	currency: {
		value: string;
		unit: string;
		highlightSymbol: string;
	};
	points: number;
}

export interface INDRACurrency {
	name: string;
	unit: string;
	highlightSymbol: string;
}

export class INDRABookingAvailabilityCalendarRecon {
	data: INDRADailyCost[];
	constructor(response: INDRABFMBookingAvailabilityCalendarResponse, dateService: INDRADateService) {
		this.data = [];
		if (response && response.data && response.data.bestPriceForDates) {
			const firstDate = dateService.INDRA_parseZone(response.data.bestPriceForDates[0].departureDate.toString());
			const priceForDatesLength = response.data.bestPriceForDates.length;
			const lastDate = dateService.INDRA_parseZone(response.data.bestPriceForDates[priceForDatesLength - 1].departureDate.toString());
			const differenceInDays = dateService.INDRA_computeDifferenceInDays(firstDate.toString(), lastDate.toString());
			const iteratingDate = firstDate;
			for (let i = 1; i <= differenceInDays; i++) {
				// this finds if next iterated date is present on the calendar API response..
				const priceForDateItem = response.data.bestPriceForDates.find(priceItem => dateService.INDRA_isSameDay(priceItem.departureDate.toString(), iteratingDate.toString()));
				if (priceForDateItem) {
					this.data.push({
						date: new Date(iteratingDate.toString()),
						ammount: {
							currency: {
								value: priceForDateItem.bestTotalPrice,
								unit: getCurrencySymbol(priceForDateItem.currency, 'wide'),
								name: priceForDateItem.currency
							},
							points: priceForDateItem.bestTotalMiles
						},
						no_flights: null
					});
				} else {
					// fills in the missing date so its date cell will still be created on the calendar..
					this.data.push({
						date: new Date(iteratingDate.toString()),
						ammount: null,
						no_flights: null
					});
				}
				iteratingDate.add(1, 'days'); // computes the next iterating date..
			}
		}
	}
}

export class INDRABookingAvailabilityCalendarReturnsRecon {
	data: INDRADailyCost[];
	constructor(response: INDRABFMBookingAvailabilityCalendarReturnsResponse) {
		this.data = [];
		if (response && response.data && response.data.returns) {
			response.data.returns.forEach(priceItem => {
				this.data.push({
					date: new Date(priceItem.returnDate),
					ammount: {
						currency: {
							value: priceItem.price,
							unit: getCurrencySymbol(sessionStorage.getItem(SESSION_VAR_KEY_CURRENCY), 'wide'),
							name: sessionStorage.getItem(SESSION_VAR_KEY_CURRENCY)
						},
						points: priceItem.miles
					},
					no_flights: null
				});
			});
		}
	}
}


export class INDRABookingAvailabilityBlankCalendar {
	data: INDRADailyCost[];
	constructor(dateService: INDRADateService) {
		this.data = [];
		const iteratingDate = moment().parseZone(); // fix moment deprecation issue
		for (let i = 1; i <= 360; i++) {
			this.data.push({
				date: new Date(iteratingDate.toString()),
				ammount: null, // null amount will be reserved for calendar API error scenario..
				no_flights: null
			});
			iteratingDate.add(1, 'days'); // computes the next iterating date..
		}
	}
}
